import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { api } from '../lib/api';
import { ImageUpload } from '../components/ImageUpload';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Send, Loader2, Plane, Hotel, Ticket, Search, X, Map, Wifi, Car, ShieldCheck, AlertCircle } from 'lucide-react';

const TRIP_TYPES = ['luxury', 'budget', 'adventure', 'food', 'family', 'honeymoon', 'solo', 'backpacking', 'nightlife', 'culture', 'road trip', 'creator special'];
// Provider-agnostic component types. Icon + label per vertical; the builder queries search_v2
// which returns a normalized `items[]` and a per-vertical connectivity/pricing status.
const VERTICALS = [
  ['flight', 'Flights', Plane], ['hotel', 'Hotels', Hotel], ['experience', 'Experiences', Ticket],
  ['tour', 'Tours', Map], ['esim', 'eSIM', Wifi], ['insurance', 'Insurance', ShieldCheck],
  ['transfer', 'Transfers', Car], ['car_rental', 'Car Rentals', Car],
];
const STATUS_STYLE = {
  LIVE: 'bg-emerald-500/12 text-emerald-600 border-emerald-500/30',
  DEMO: 'bg-accent/12 text-accent border-accent/30',
  INDICATIVE: 'bg-amber-500/12 text-amber-600 border-amber-500/30',
};

const emptyDay = (n) => ({ day_id: `d${n}_${Math.random().toString(36).slice(2, 7)}`, day: n, title: '', description: '', morning: '', afternoon: '', evening: '', tips: '', activities: [], offers: [] });

export default function CreatorBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === 'new';
  const { user, loading } = useAuth();
  const [saving, setSaving] = useState(false);
  const [it, setIt] = useState({ title: '', short_description: '', cover_image: '', destination: '', destination_code: '', country: '', duration_days: 3, trip_type: 'creator special', best_time: '', estimated_budget: '', currency: 'INR', traveller_type: '', days: [emptyDay(1)] });
  const [itineraryId, setItineraryId] = useState(isNew ? null : id);

  useEffect(() => { document.title = 'Itinerary Builder | FlightDeals.in'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  useEffect(() => {
    if (!isNew && user) api.creatorGetItinerary(id).then(({ data }) => {
      const d = data.itinerary; setIt({ ...d, days: d.days?.length ? d.days : [emptyDay(1)] });
    }).catch(() => { toast.error('Not found'); navigate('/creator/dashboard'); });
  }, [id, isNew, user, navigate]);

  const upd = (k, v) => setIt((s) => ({ ...s, [k]: v }));
  const updDay = (i, k, v) => setIt((s) => { const days = [...s.days]; days[i] = { ...days[i], [k]: v }; return { ...s, days }; });
  const addDay = () => setIt((s) => ({ ...s, days: [...s.days, emptyDay(s.days.length + 1)] }));
  const removeDay = (i) => setIt((s) => ({ ...s, days: s.days.filter((_, idx) => idx !== i).map((d, idx) => ({ ...d, day: idx + 1 })) }));

  const payload = () => ({ ...it, estimated_budget: it.estimated_budget ? Number(it.estimated_budget) : null, duration_days: Number(it.duration_days) || it.days.length });

  const save = async (submit = false) => {
    if (!it.title) { toast.error('Add a title'); return; }
    setSaving(true);
    try {
      let iid = itineraryId;
      if (!iid) { const { data } = await api.creatorCreateItinerary(payload()); iid = data.itinerary.itinerary_id; setItineraryId(iid); }
      else { await api.creatorUpdateItinerary(iid, payload()); }
      if (submit) { await api.creatorSubmitItinerary(iid); toast.success('Submitted for review!'); navigate('/creator/dashboard'); return; }
      toast.success('Saved'); if (isNew) navigate(`/creator/itineraries/${iid}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-5">
          <h1 className="font-display text-2xl font-semibold">{isNew ? 'Create itinerary' : 'Edit itinerary'}</h1>
          <div className="flex gap-2">
            <Button onClick={() => save(false)} disabled={saving} variant="outline" className="rounded-xl" data-testid="save-draft-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save draft</Button>
            <Button onClick={() => save(true)} disabled={saving} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="submit-review-button"><Send className="h-4 w-4 mr-1.5" /> Submit for review</Button>
          </div>
        </div>

        <Card className="p-5 rounded-2xl space-y-3 mb-5">
          <div><Label>Title</Label><Input value={it.title} onChange={(e) => upd('title', e.target.value)} placeholder="7 Days in Tokyo — My Ultimate First-Timer Guide" data-testid="builder-title" /></div>
          <div><Label>Short description</Label><Textarea rows={2} value={it.short_description} onChange={(e) => upd('short_description', e.target.value)} data-testid="builder-desc" /></div>
          <div><Label>Cover image</Label><ImageUpload variant="cover" value={it.cover_image} onChange={(url) => upd('cover_image', url)} testId="builder-cover" /><Input value={it.cover_image} onChange={(e) => upd('cover_image', e.target.value)} placeholder="…or paste an image URL" className="mt-2" data-testid="builder-cover-url" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label>Destination</Label><Input value={it.destination} onChange={(e) => upd('destination', e.target.value)} placeholder="Tokyo" data-testid="builder-destination" /></div>
            <div><Label>Airport/City code</Label><Input value={it.destination_code} onChange={(e) => upd('destination_code', e.target.value.toUpperCase())} placeholder="TYO" data-testid="builder-code" /></div>
            <div><Label>Country</Label><Input value={it.country} onChange={(e) => upd('country', e.target.value)} placeholder="Japan" data-testid="builder-country" /></div>
            <div><Label>Duration (days)</Label><Input type="number" min={1} value={it.duration_days} onChange={(e) => upd('duration_days', e.target.value)} data-testid="builder-duration" /></div>
            <div><Label>Trip type</Label><Select value={it.trip_type} onValueChange={(v) => upd('trip_type', v)}><SelectTrigger data-testid="builder-triptype"><SelectValue /></SelectTrigger><SelectContent>{TRIP_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Est. budget</Label><Input type="number" value={it.estimated_budget} onChange={(e) => upd('estimated_budget', e.target.value)} data-testid="builder-budget" /></div>
          </div>
          <div><Label>Best time to visit</Label><Input value={it.best_time} onChange={(e) => upd('best_time', e.target.value)} placeholder="Mar–May & Oct–Nov" data-testid="builder-besttime" /></div>
        </Card>

        <div className="space-y-4">
          {it.days.map((day, i) => (
            <Card key={day.day_id} className="p-5 rounded-2xl" data-testid={`builder-day-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">Day {day.day}</span>
                {it.days.length > 1 && <Button size="icon" variant="ghost" onClick={() => removeDay(i)} data-testid={`remove-day-${i}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
              <div className="space-y-2">
                <Input value={day.title} onChange={(e) => updDay(i, 'title', e.target.value)} placeholder="Day title, e.g. Explore Shibuya" data-testid={`day-title-${i}`} />
                <Textarea rows={2} value={day.description} onChange={(e) => updDay(i, 'description', e.target.value)} placeholder="Overview of the day" />
                <div className="grid grid-cols-3 gap-2">
                  <Input value={day.morning} onChange={(e) => updDay(i, 'morning', e.target.value)} placeholder="Morning" />
                  <Input value={day.afternoon} onChange={(e) => updDay(i, 'afternoon', e.target.value)} placeholder="Afternoon" />
                  <Input value={day.evening} onChange={(e) => updDay(i, 'evening', e.target.value)} placeholder="Evening" />
                </div>
                <Input value={day.tips} onChange={(e) => updDay(i, 'tips', e.target.value)} placeholder="Creator tip" />
                {/* Offers */}
                <div className="flex flex-wrap gap-2 items-center pt-1">
                  {(day.offers || []).map((o, oi) => (
                    <span key={oi} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs" data-testid={`day-${i}-offer-${oi}`}>
                      {o.price_status && <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${o.price_status === 'LIVE' ? 'text-emerald-600 border-emerald-500/40' : o.price_status === 'INDICATIVE' ? 'text-amber-600 border-amber-500/40' : 'text-accent border-accent/40'}`}>{o.price_status}</span>}
                      {o.title || o.name} <button onClick={() => updDay(i, 'offers', day.offers.filter((_, x) => x !== oi))}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                  <AddOfferDialog destination={it.destination} destinationCode={it.destination_code} onAdd={(offer) => updDay(i, 'offers', [...(day.offers || []), offer])} dayIndex={i} />
                </div>
              </div>
            </Card>
          ))}
          <Button onClick={addDay} variant="outline" className="w-full rounded-xl border-dashed" data-testid="add-day-button"><Plus className="h-4 w-4 mr-1.5" /> Add day</Button>
        </div>
      </div>
    </Layout>
  );
}

function AddOfferDialog({ destination, destinationCode, onAdd, dayIndex }) {
  const [open, setOpen] = useState(false);
  const [vertical, setVertical] = useState('experience');
  const [origin, setOrigin] = useState('CCU');
  const [result, setResult] = useState(null); // { status, connected, items, message }
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    setResult(null);
    try {
      const params = { vertical, destination, destination_code: destinationCode, destination_slug: destination };
      if (vertical === 'flight') params.origin = origin;
      const { data } = await api.creatorOfferSearchV2(params);
      setResult(data);
      if (data.status === 'INPUT_REQUIRED') toast.error(data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
    } finally { setLoading(false); }
  };

  const pick = async (o) => {
    // Register the canonical FD product first so we can persist its fd_id on the item — this is
    // what powers the cross-provider price compare on the public itinerary.
    const reg = await api.creatorRegisterProduct({
      vertical: o.vertical, title: o.title, offer_id: o.offer_id, provider: o.provider,
      provider_product_id: o.provider_product_id, deep_link: o.deep_link,
      price: o.price, currency: o.currency, price_status: o.price_status, meta: o.meta,
    });
    const fd_id = reg?.data?.fd_id || '';
    // Store the normalized component. `kind` mirrors the vertical so existing render/CTA works;
    // provider identity (provider + provider_product_id) is preserved separately.
    onAdd({
      kind: o.vertical, vertical: o.vertical, offer_id: o.offer_id, fd_id,
      title: o.title, name: o.title, subtitle: o.subtitle,
      price: o.price, currency: o.currency, price_status: o.price_status,
      provider: o.provider, provider_product_id: o.provider_product_id,
      deep_link: o.deep_link, image: o.image, type: o.vertical, details: o.details || {},
    });
    setOpen(false); setResult(null);
    toast.success('Added to itinerary');
  };

  const changeVertical = (v) => { setVertical(v); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`add-offer-${dayIndex}`}><Plus className="h-3 w-3 mr-1" /> Add item</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add itinerary item</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Add flights, stays, experiences, tours, eSIMs & more. Only connected inventory can be added; a secure tracking link is generated automatically.</p>
        <div className="flex gap-2 flex-wrap">
          {VERTICALS.map(([v, l, Ic]) => (
            <button key={v} onClick={() => changeVertical(v)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${vertical === v ? 'bg-accent/12 text-accent border-accent/30' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`} data-testid={`offerkind-${v}`}><Ic className="h-3.5 w-3.5 inline mr-1" />{l}</button>
          ))}
        </div>
        {vertical === 'flight' && <div><Label>Origin code</Label><Input value={origin} onChange={(e) => setOrigin(e.target.value.toUpperCase())} placeholder="CCU" data-testid="offer-origin-input" /></div>}
        <Button onClick={search} disabled={loading} className="rounded-xl bg-primary text-primary-foreground" data-testid="offer-search-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />} Search inventory</Button>

        {result && !result.connected && (
          <div className="rounded-xl border border-dashed border-border p-4 flex items-start gap-3" data-testid="offer-not-connected">
            <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Not available yet</p>
              <p className="text-xs text-muted-foreground">{result.message || 'This provider isn\'t connected yet.'}</p>
            </div>
          </div>
        )}

        {result && result.connected && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{result.count} result{result.count === 1 ? '' : 's'}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLE[result.status] || 'bg-secondary text-muted-foreground border-border'}`} data-testid="offer-status-badge">{result.status}</span>
            </div>
            <div className="max-h-64 overflow-auto space-y-2" data-testid="offer-search-results">
              {result.items.map((o) => (
                <button key={o.offer_id} onClick={() => pick(o)} className="w-full text-left rounded-xl border border-border p-3 hover:bg-secondary flex items-center justify-between gap-3" data-testid={`offer-result-${o.offer_id}`}>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium truncate">{o.title}</span>
                    <span className="block text-xs text-muted-foreground truncate">{o.subtitle ? `${o.subtitle} · ` : ''}via {o.provider || 'provider'}</span>
                  </span>
                  <span className="text-sm font-semibold tabular-nums shrink-0">{o.price != null ? `${o.currency} ${Math.round(o.price)}` : '—'}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
